// customization:
// - render event_period_text se compilato, gestione date se campo vuoto
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import { rrulestr } from 'rrule';
import { rrulei18n } from '@plone/volto/components/manage/Widgets/RecurrenceWidget/Utils';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import config from '@plone/registry';
// custom
import { RichText } from '@redturtle/iovisit-theme/components/Views';

const messages = defineMessages({
  eventFrom: {
    id: 'eventFrom',
    defaultMessage: 'Dal',
  },
  eventFromV: {
    id: 'eventFromV',
    defaultMessage: "Dall'",
  },
  eventTo: {
    id: 'eventTo',
    defaultMessage: 'al',
  },
  eventToV: {
    id: 'eventToV',
    defaultMessage: "all'",
  },
  eventFromTime: {
    id: 'eventFromTime',
    defaultMessage: 'dalle',
  },
  eventToTime: {
    id: 'eventToTime',
    defaultMessage: 'alle',
  },
  eventOn: {
    id: 'eventOn',
    defaultMessage: 'Il',
  },
  eventOnV: {
    id: 'eventOnV',
    defaultMessage: "L'",
  },
});

const datesForDisplay = (start, end, locale) => {
  const timezone = config.settings.timezone || 'UTC';
  momentTZ.defineLocale(locale, moment.localeData(locale)._config);

  const mStart = momentTZ.tz(start, timezone);

  const mEnd = momentTZ.tz(end, timezone);
  if (!mStart.isValid() || !mEnd.isValid()) {
    return null;
  }
  const sameYear = mStart.isSame(mEnd, 'year');
  const sameMonth = mStart.isSame(mEnd, 'month');
  const sameDay = mStart.isSame(mEnd, 'day');
  const sameTime =
    mStart.hour() === mEnd.hour() && mStart.minute() === mEnd.minute();
  const noStartTime = mStart.hour() === 0 && mStart.minute() === 0;
  const noEndTime = mEnd.hour() === 0 && mEnd.minute() === 0;
  const wholeDay = noStartTime && noEndTime && sameTime;

  return {
    sameYear,
    sameMonth,
    sameDay,
    sameTime,
    wholeDay,
    mStart,
    mEnd,
  };
};

const WhenComponent = ({
  start,
  end,
  show_times = true,
  whole_day,
  open_end,
  recurrence,
  className,
  listing = false,
  moment,
  event_period_text,
}) => {
  const intl = useIntl();
  const Moment = moment.default;
  Moment.locale(intl.locale);

  if ((!start && !end) || !start) {
    return null;
  }
  const datesInfo = datesForDisplay(start, end, intl.locale);
  if (!datesInfo) {
    // eslint-disable-next-line no-console
    console.warn('EventWhen: Received invalid start or end date.');
    return null;
  }
  const { sameYear, sameMonth, sameDay, sameTime, wholeDay, mStart, mEnd } =
    datesInfo;

  const allDay = whole_day || wholeDay;

  if (recurrence) {
    // custom
    // if there's a description of the recurrence
    // show that and not the actual calculated recurrence
    if (!!RichText({ content: event_period_text })) {
      return RichText({ content: event_period_text });
    }

    let rruleSet = rrulestr(recurrence, {
      compatible: true, //If set to True, the parser will operate in RFC-compatible mode. Right now it means that unfold will be turned on, and if a DTSTART is found, it will be considered the first recurrence instance, as documented in the RFC.
      forceset: true,
    });
    if (open_end) {
      rruleSet._rrule[0].options.until = null;
      rruleSet._rrule[0].origOptions.until = null;
    }

    const RRULE_LANGUAGE = rrulei18n(intl, Moment);
    const rruleText = rruleSet.rrules()[0]?.toText(
      (t) => {
        return RRULE_LANGUAGE.strings[t];
      },
      RRULE_LANGUAGE,
      RRULE_LANGUAGE.dateFormatter,
    );

    return (
      <div
        className={cx('when', {
          [className]: className !== null,
          'same-year': sameYear,
          'same-month': sameMonth,
          'same-day': sameDay,
          'same-time': sameTime,
          'whole-day': allDay,
          'open-end': open_end,
        })}
      >
        {listing
          ? [1, 8, 11].indexOf(mStart.date()) >= 0
            ? intl.formatMessage(messages.eventOnV)
            : intl.formatMessage(messages.eventOn)
          : [1, 8, 11].indexOf(mStart.date()) >= 0
            ? intl.formatMessage(messages.eventFromV)
            : intl.formatMessage(messages.eventFrom)}{' '}
        {mStart.format('DD/MM/YYYY')}
        {rruleText && <div className="recurrence">{rruleText}</div>}
        {!allDay || (!open_end && !sameTime && <br />)}
        {!allDay && (
          <>
            {!rruleText && <>&nbsp;</>}
            {show_times && (
              <>
                {intl.formatMessage(messages.eventFromTime)}
                &nbsp;
                <span className="start-time">{mStart.format('H:mm')}</span>
              </>
            )}
          </>
        )}
        {!allDay && !open_end && !sameTime && show_times && (
          <>
            &nbsp;
            {intl.formatMessage(messages.eventToTime)}
            &nbsp;
            <span className="end-time">{mEnd.format('H:mm')}</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={cx('when', {
        [className]: className !== null,
        'same-year': sameYear,
        'same-month': sameMonth,
        'same-day': sameDay,
        'same-time': sameTime,
        'whole-day': allDay,
        'open-end': open_end,
      })}
    >
      {/* custom */}
      {!!RichText({ content: event_period_text }) ? (
        RichText({ content: event_period_text })
      ) : !sameDay ? (
        <>
          {[1, 8, 11].indexOf(mStart.date()) >= 0
            ? intl.formatMessage(messages.eventFromV)
            : intl.formatMessage(messages.eventFrom)}
          &nbsp;
          <span className="start">
            <span className="start-date">
              {mStart.format(
                sameMonth && !open_end
                  ? intl.locale === 'en'
                    ? 'MMMM D'
                    : 'D'
                  : 'DD/MM/YYYY',
              )}
            </span>

            {open_end && show_times && (
              <>
                <span> {intl.formatMessage(messages.eventFromTime)} </span>
                <span className="start-time">{mStart.format('DD')}</span>
              </>
            )}
          </span>
          {!open_end && (
            <>
              &nbsp;
              {[1, 8, 11].indexOf(mEnd.date()) >= 0
                ? intl.formatMessage(messages.eventToV)
                : intl.formatMessage(messages.eventTo)}
              &nbsp;
              <span className="end">
                <span className="end-date">{mEnd.format('DD/MM/YYYY')}</span>
                {!allDay && show_times && (
                  <>
                    {/* Plone has an optional word based on locale here */}

                    <span> </span>
                    {sameTime ? (
                      <span className="end-time">
                        {mEnd.format('DD/MM/YYYY')}
                      </span>
                    ) : (
                      <>
                        {intl.formatMessage(messages.eventFromTime)}{' '}
                        <span className="start-time">
                          {mStart.format('LT')}
                        </span>{' '}
                        {intl.formatMessage(messages.eventToTime)}{' '}
                        <span className="start-time">{mEnd.format('LT')}</span>
                      </>
                    )}
                  </>
                )}
              </span>
            </>
          )}
        </>
      ) : (
        <>
          {allDay && (
            <span className="start-date">{mStart.format('DD/MM/YYYY')}</span>
          )}
          {open_end && !allDay && (
            <>
              {[1, 8, 11].indexOf(mStart.date()) >= 0
                ? intl.formatMessage(messages.eventFromV)
                : intl.formatMessage(messages.eventFrom)}
              &nbsp;
              <span className="start-date">{mStart.format('DD/MM/YYYY')}</span>
              {show_times && (
                <>
                  &nbsp;
                  {intl.formatMessage(messages.eventFromTime)}
                  &nbsp;
                  <span className="start-time">
                    {mStart.format('DD/MM/YYYY')}
                  </span>
                </>
              )}
            </>
          )}
          {!(allDay || open_end) && (
            <>
              <span className="start-date">{mStart.format('DD/MM/YYYY')}</span>

              {show_times && (
                <>
                  &nbsp;
                  {intl.formatMessage(messages.eventFromTime)}
                  &nbsp;
                  <span className="start-time">{mStart.format('LT')}</span>
                  &nbsp;
                  {intl.formatMessage(messages.eventToTime)}
                  &nbsp;
                  <span className="end-time">{mEnd.format('LT')}</span>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

const When = injectLazyLibs(['moment'])(WhenComponent);

When.propTypes = {
  start: PropTypes.string.isRequired,
  end: PropTypes.string,
  allDay: PropTypes.bool,
  open_end: PropTypes.bool,
};

export { When, datesForDisplay };
